// `import type` mizí při buildu, takže z něj nevznikne runtime závislost — právě o to tu jde.
import type { api as TApi } from "api/generated";

import { CALENDAR_TIMETABLE_TAG_TYPE, calendarTimetableTag } from "./calendarTimetableTag";

/**
 * Jak dlouho po odhlášení poslední komponenty zůstane odpověď v cache, ve vteřinách.
 *
 * RTK Query má výchozích 60 s a `createApi` v `api.tsx` je nepřepisuje. Katalog knih se
 * přitom mění jen z CMS, takže odejít na dvě minuty a vrátit se dnes znamená stáhnout
 * znovu 141 kB. Patnáct minut je kompromis: delší než jakákoli běžná odbočka v aplikaci,
 * kratší než sezení, takže editor v CMS uvidí změnu ještě týž den bez tvrdého reloadu.
 */
export const VYCHOZI_KEEP_UNUSED_DATA_FOR = 900;

/**
 * Dotazy, jejichž odpověď je čistý obsah z CMS — pro všechny studenty stejný a nezávislý
 * na tom, co student zrovna udělal.
 *
 * AIDEV-NOTE: Seznam je uzavřený schválně, ne heuristika podle jména. Generovaná api nemá
 * `tagTypes` (`api.tsx:58-88`) a tagy přidává až `applyCachePolicy` níž, takže ostatní
 * mutace nic neinvalidují — delší cache u dotazu vázaného na studenta by znamenala, že po
 * uložení uvidí svůj starý stav. Nejnebezpečnější kandidát je `getMySelectionOverview`:
 * vypadá katalogově, ale nemá argument (konstantní cache klíč) a vrací `userSelectedBooks`.
 * Do mapy nepatří a hlídá to test. [TD-362]
 *
 * `getBooksSelectionOverview` v mapě je, protože jeho klíčem je `idList` — po změně výběru
 * vznikne jiný klíč, takže zastaralý seznam vrátit nemůže. Vrací jen data knih.
 */
export const KATALOGOVE_ENDPOINTY: Record<string, number> = {
  getGraduationReadingOverview: VYCHOZI_KEEP_UNUSED_DATA_FOR,
  getBookDetail: VYCHOZI_KEEP_UNUSED_DATA_FOR,
  getBooksSelectionOverview: VYCHOZI_KEEP_UNUSED_DATA_FOR,
};

/**
 * Nastaví delší cache katalogovým dotazům.
 *
 * AIDEV-NOTE: `api` se PŘEDÁVÁ, nedováží se hodnotovým importem. `api/api.tsx` a
 * `api/generated.ts` se importují navzájem (komentář v `BooksOverviewHistory.test.tsx:9`)
 * a další statický import do toho kruhu je zbytečné riziko pro pořadí vyhodnocení modulů
 * v Rollup bundlu. `import type` se při buildu odstraní, takže kruh nevzniká.
 *
 * Volá se ze `store.ts`, tedy jednou při startu a před prvním renderem.
 * `enhanceEndpoints` MUTUJE definice už injectnutých endpointů a vrací tutéž instanci
 * (`rtk-query.legacy-esm.js:2035` — `Object.assign(getEndpointDefinition(…) || {}, …)`),
 * takže hooky importované z `api/generated` dostanou novou konfiguraci, aniž by se
 * generovaný soubor musel sahat. To `|| {}` je zároveň past: neznámé jméno endpointu se
 * zapíše do zahozeného objektu a nikde to nekřikne — proto to hlídá test. [TD-362]
 */
export const applyCachePolicy = (api: typeof TApi) => {
  api.enhanceEndpoints({
    endpoints: Object.fromEntries(
      Object.entries(KATALOGOVE_ENDPOINTY).map(([name, keepUnusedDataFor]) => [name, { keepUnusedDataFor }]),
    ),
  });

  // AIDEV-NOTE: `getTimetable` je vázaný na studenta, proto NENÍ v KATALOGOVE_ENDPOINTY, a delší cache
  // (stejných 900 s) má jen díky dvěma pojistkám v `CalendarMonthOverview`: stránka se při každém
  // připojení obnoví, pokud odpověď není mladší než CALENDAR_DATA_MAX_AGE_S, a po otevření ukáže starou
  // odpověď jen z téhož kalendářního dne (noční job ve 23:55 mění stavy minulého dne). Po uzavření tréninku
  // a po uložení plánu se záznam kalendáře invaliduje tagem (viz níž).
  //
  // Druhý uživatel, `useTimetableData` (týdenní dotaz v `ExerciseSessionArea`), delší cache ZDĚDÍ — endpoint
  // je jeden — a obnovuje se při každém připojení (`refetch()`), ale jeho první render smí číst až 15
  // minut starou odpověď (dřív nanejvýš 60 s). Rozebráno a ověřeno testem `ExerciseSessionAreaCache`: z
  // odpovědi se čte jediné, `isTrainingFinished` dneška. Stará „nehotovo“ se chová jako studený start
  // (potomek se vykreslí, po obnově se případně vymění za „Dneska už jsi úkoly zvládl“); dokončený den se
  // v průběhu dne nevrací, takže stará „hotovo“ je platná; odpověď z minulého dne obsahuje dnešek jako
  // nehotový den, takže omylem „hotovo“ neukáže. Starou odpověď proto není třeba skrývat — skrytí podle
  // času připojení by naopak při každém přemontování (`key={Math.random()}` v `userRouter`) vyměnilo
  // známé „hotovo“ za bliknutí potomka, který zakládá trénink. [TD-476]
  api.enhanceEndpoints({ endpoints: { getTimetable: { keepUnusedDataFor: VYCHOZI_KEEP_UNUSED_DATA_FOR } } });

  // AIDEV-NOTE: Tag CalendarTimetable s id = rozsah `dateFrom..dateTo`. Žádná mutace ho nevyvolává; invaliduje ho
  // ručně `Summary` po úspěšném sessionClose, a to JEN s rozsahem kalendáře (`getCalendarRange`), takže týdenní
  // dotaz `useTimetableData` v `ExerciseSessionArea` (jiný rozsah, vždy připojený) se nenačte znovu — jeho
  // refetch by přehled výsledků vyměnil za obrazovku „Dneska už jsi úkoly zvládl“. Pro RTK Query 2.11
  // (`invalidationBehavior: "delayed"`, výchozí) platí: záznam BEZ odběratelů (prefetch z menu během tréninku)
  // se smaže, další návštěva kalendáře začne spinnerem, ne zastaralými daty; záznam S odběratelem se načte
  // znovu; invalidace se odloží, dokud běží jakýkoli dotaz nebo mutace, a provede se po jeho dokončení — dotaz
  // na kalendář rozjetý před uzavřením tréninku tedy nezůstane zastaralý (`refetchQuery` za běhu by se zahodil,
  // `condition` thunku nepustí dotaz ve stavu pending). Rozsahy kalendáře a týdne se shodují jen tehdy, když
  // je termín v posledním týdnu měsíce a plán vznikl v tomto týdnu — pak jde o jeden záznam. [TD-476]
  //
  // AIDEV-NOTE: Tag UserBookSelection. Přehled „Moje knihy" má dotaz během editace ve `skip`, RTK
  // Query ale jeho odpověď drží ještě 60 s — návrat po rychlém uložení tak ukázal výběr před
  // editací a „seznam není v pořádku" (produkce 21. 9., uživatel 49271). Invalidace záznam bez
  // odběratelů smaže, s odběrateli ho načte znovu. [TD-434]
  //
  // AIDEV-NOTE: Tag CurrentUser má jen `providesTags`, žádná mutace ho nevyvolává (`invalidatesTags`).
  // Počítadla v liště čtou user slice, který plní jen getCurrentUser, takže po tréninku zůstávala
  // stará. Invaliduje ho ručně `Summary` po úspěšném sessionClose; `invalidatesTags` na mutaci by
  // načetl uživatele i po sessionClose z Intro.tsx, které se volá při každém otevření /cviceni
  // (uzavírá starou session), tedy asi 1800× denně zbytečně. [TD-472]
  api.enhanceEndpoints({
    addTagTypes: ["UserBookSelection", "CurrentUser", CALENDAR_TIMETABLE_TAG_TYPE],
    endpoints: {
      getMySelectionOverview: { providesTags: ["UserBookSelection"] },
      setUserBooks: { invalidatesTags: ["UserBookSelection"] },
      getCurrentUser: { providesTags: ["CurrentUser"] },
      getTimetable: { providesTags: (_result, _error, arg) => [calendarTimetableTag(arg)] },
      // Změna plánu mění dny kalendáře; tag bez id zasáhne všechny záznamy `getTimetable`, tedy i týdenní —
      // ten je po změně plánu potřeba obnovit také a po `setTimetableGeneric` není na obrazovce výsledků.
      setTimetableGeneric: { invalidatesTags: [{ type: CALENDAR_TIMETABLE_TAG_TYPE }] },
    },
  });
};
